import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type ClinicSettings } from '../api'
import { useAuth } from '../auth'
import { rupees } from '../quickBill'
import { today, useRoleFlags } from './mobile/shared'
import '../home.css'

/* The staff home page: today at a glance, with the bill one click away.
 * Every figure links to the screen that holds the detail, so the desk starts
 * here and goes wherever the day needs. A failed figure is left as a dash. */

interface BillRow { id: string; invoiceNo: string; total: string; createdAt: string; patientName: string; outstanding: string; settlement: string; status: string }
interface QueueRow { id: string; tokenNo: number; status: string; patientName: string; doctorName: string }
interface LabRow { id: string; status: string; testName: string; patientName: string; serialNo: number | null }

const sameDay = (iso: string, day: string) => new Date(iso).toLocaleDateString('en-CA') === day

export default function StaffHome() {
  const { user } = useAuth()
  const { canBill } = useRoleFlags()
  const [settings, setSettings] = useState<ClinicSettings | null>(null)
  const [collected, setCollected] = useState<string | null>(null)
  const [bills, setBills] = useState<BillRow[] | null>(null)
  const [queue, setQueue] = useState<QueueRow[] | null>(null)
  const [lab, setLab] = useState<LabRow[] | null>(null)

  useEffect(() => {
    const day = today()
    let live = true
    void Promise.allSettled([
      api.get<{ settings: ClinicSettings | null }>('/api/settings'),
      canBill ? api.get<{ total: string }>('/api/reports/collection') : Promise.reject(new Error('no billing')),
      canBill ? api.get<{ invoices: BillRow[] }>('/api/invoices?limit=100') : Promise.reject(new Error('no billing')),
      api.get<{ visits: QueueRow[] }>('/api/visits'),
      api.get<{ bookings: LabRow[] }>(`/api/lab-bookings?date=${day}`),
    ]).then(([s, c, b, v, l]) => {
      if (!live) return
      if (s.status === 'fulfilled') setSettings(s.value.settings)
      if (c.status === 'fulfilled') setCollected(c.value.total)
      if (b.status === 'fulfilled') setBills(b.value.invoices.filter((r) => r.status !== 'void' && sameDay(r.createdAt, day)))
      if (v.status === 'fulfilled') setQueue(v.value.visits)
      if (l.status === 'fulfilled') setLab(l.value.bookings)
    })
    return () => { live = false }
  }, [canBill])

  const unpaid = (bills ?? []).filter((b) => b.settlement !== 'paid')
  const due = unpaid.reduce((s, b) => s + Number(b.outstanding), 0)
  const waiting = (queue ?? []).filter((v) => v.status === 'waiting')
  const labOpen = (lab ?? []).filter((r) => r.status === 'booked')
  const apptsOn = settings?.appointmentsEnabled !== false
  const n = (v: unknown[] | null) => (v === null ? '—' : String(v.length))

  const shortcuts: { to: string; label: string; sub: string }[] = [
    ...(canBill ? [{ to: '/billing', label: 'Bills', sub: 'Every bill, paid and unpaid' }] : []),
    { to: '/reception', label: 'Reception', sub: 'Register and send to a doctor' },
    { to: '/investigations', label: 'Lab tests', sub: 'Today’s lab sheet and results' },
    ...(apptsOn ? [{ to: '/appointments', label: 'Appointments', sub: 'Bookings by doctor and day' }] : []),
    { to: '/records', label: 'Records', sub: 'Patients and past visits' },
  ]

  return (
    <>
      <div className="page-head home-head">
        <div>
          <h2>{settings?.clinicName ?? user?.clinicName ?? 'Home'}</h2>
          <p>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })} · signed in as {user?.fullName}</p>
        </div>
        {canBill && <Link to="/bill/new" className="home-bill-btn">+ New bill</Link>}
      </div>

      <div className="home-stats">
        {canBill && (
          <Link to="/billing" className="stat-card home-stat">
            <div className="stat-label">Collected today</div>
            <div className="stat-value">{collected === null ? '—' : rupees(collected)}</div>
            <div className="stat-sub">{bills === null ? '' : `${bills.length} bill${bills.length === 1 ? '' : 's'} today`}</div>
          </Link>
        )}
        {canBill && (
          <Link to="/billing" className="stat-card home-stat">
            <div className="stat-label">Unpaid today</div>
            <div className="stat-value">{bills === null ? '—' : rupees(due)}</div>
            <div className="stat-sub">{bills === null ? '' : `${unpaid.length} bill${unpaid.length === 1 ? '' : 's'} to collect`}</div>
          </Link>
        )}
        <Link to="/reception" className="stat-card home-stat">
          <div className="stat-label">Waiting for a doctor</div>
          <div className="stat-value">{n(queue === null ? null : waiting)}</div>
          <div className="stat-sub">{queue === null ? '' : `${queue.length} visit${queue.length === 1 ? '' : 's'} today`}</div>
        </Link>
        <Link to="/investigations" className="stat-card home-stat">
          <div className="stat-label">Lab tests to do</div>
          <div className="stat-value">{n(lab === null ? null : labOpen)}</div>
          <div className="stat-sub">{lab === null ? '' : `${lab.length} on today’s sheet`}</div>
        </Link>
      </div>

      <div className="home-grid">
        {canBill && (
          <section className="panel home-list">
            <div className="home-list-head"><h3>Today’s bills</h3><Link to="/billing">All bills</Link></div>
            {bills === null ? <div className="empty">—</div>
              : bills.length === 0 ? <div className="empty">No bills yet today. <Link to="/bill/new">Make the first one</Link>.</div>
              : (
                <ul>
                  {bills.slice(0, 8).map((b) => (
                    <li key={b.id}>
                      <div><b>{b.patientName}</b><small>{b.invoiceNo} · {new Date(b.createdAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}</small></div>
                      <span className="num">{rupees(b.total)}</span>
                      <span className={`pill ${b.settlement === 'paid' ? 'on' : 'warn'}`}>{b.settlement === 'paid' ? 'Paid' : 'Due'}</span>
                    </li>
                  ))}
                </ul>
              )}
          </section>
        )}

        <section className="panel home-list">
          <div className="home-list-head"><h3>Lab sheet</h3><Link to="/investigations">Open</Link></div>
          {lab === null ? <div className="empty">—</div>
            : labOpen.length === 0 ? <div className="empty">{lab.length ? 'Every test today is done.' : 'No tests booked today.'}</div>
            : (
              <ul>
                {labOpen.slice(0, 8).map((r) => (
                  <li key={r.id}>
                    <div><b>{r.testName}</b><small>{r.patientName}</small></div>
                    {r.serialNo != null && <span className="pill off">#{r.serialNo}</span>}
                  </li>
                ))}
              </ul>
            )}
        </section>

        <section className="panel home-list">
          <div className="home-list-head"><h3>Waiting</h3><Link to="/reception">Reception</Link></div>
          {queue === null ? <div className="empty">—</div>
            : waiting.length === 0 ? <div className="empty">Nobody is waiting.</div>
            : (
              <ul>
                {waiting.slice(0, 8).map((v) => (
                  <li key={v.id}>
                    <div><b>{v.patientName}</b><small>{v.doctorName}</small></div>
                    <span className="pill off">Token {v.tokenNo}</span>
                  </li>
                ))}
              </ul>
            )}
        </section>

        <section className="panel home-list">
          <div className="home-list-head"><h3>Go to</h3></div>
          <div className="home-links">
            {shortcuts.map((s) => (
              <Link key={s.to} to={s.to}><b>{s.label}</b><small>{s.sub}</small></Link>
            ))}
          </div>
        </section>
      </div>
    </>
  )
}
